/**
 * MaskedName
 *
 * Renders an accused person's name with role-aware visual privacy:
 *
 *   admin / super-admin → full name, plain text
 *   user (authenticated) → first name visible, last name(s) CSS-blurred
 *   guest / public       → "Anonymous" (dimmed italic)
 *
 * Use this component wherever JSX is rendered.
 * For string-only contexts (map popup HTML, SVG text) use maskName() instead.
 */
import type { ViewerRole } from '../utils/privacy';

interface Props {
  name: string | null | undefined;
  role: ViewerRole;
  className?: string;
}

export default function MaskedName({ name, role, className = '' }: Props) {
  if (!name?.trim()) return <span className={className}>—</span>;

  const r = (role ?? '').toLowerCase();

  /* Admin sees everything */
  if (r === 'admin' || r === 'super-admin') {
    return <span className={className}>{name.trim()}</span>;
  }

  /* Guest sees nothing identifying */
  if (r !== 'user') {
    return (
      <span className={`text-white/25 italic text-[0.9em] ${className}`}>
        Anonymous
      </span>
    );
  }

  /* Authenticated user: first name visible, surname(s) blurred */
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) {
    return <span className={className}>{parts[0] ?? '—'}</span>;
  }

  const firstName = parts[0];
  const surname   = parts.slice(1).join(' '); // non-breaking spaces keep it one block

  return (
    <span className={`inline-flex items-baseline gap-[0.3em] ${className}`}>
      <span>{firstName}</span>
      <span className="relative inline-block">
        {/* Blurred surname — visually present, unreadable */}
        <span
          className="select-none pointer-events-none"
          style={{
            filter:      'blur(4px)',
            opacity:     0.55,
            userSelect:  'none',
            WebkitUserSelect: 'none',
          }}
          aria-hidden="true"
        >
          {surname}
        </span>
        {/* Invisible screen-reader label */}
        <span className="sr-only">surname hidden</span>
      </span>
    </span>
  );
}
